import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Paper,
  Divider,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  HelpOutlineOutlined as HelpIcon,
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Mic as MicIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import ModalXLogo from "../assets/ModalX_-orange_Icon.png";
import HeygenLogo from "../assets/Heygen/Heygen Logo.png";
import { SvgIcon } from "@mui/material";

import AvatarCreation from "../components/workspace/settingBar/HeyGenSettings.jsx/AvatarCreation/AvatarCreation";
import CaptionSettingsForHeygen from "../components/workspace/settingBar/HeyGenSettings.jsx/CaptionSettings";
import LanguageAndVoiceForHeygen from "../components/workspace/settingBar/HeyGenSettings.jsx/LanguageAndVoiceForHeygen";
import HeygenVideoBackground from "../components/workspace/settingBar/HeyGenSettings.jsx/HeygenVideoBackground";
import DimensionsSettings from "../components/workspace/settingBar/HeyGenSettings.jsx/DimensionsSettings";

import sourceUrl from "../api/Source.api";
import { useWorkspace } from "../contexts/WorkspaceContext";

function GradientAIIcon() {
  return (
    <SvgIcon>
      <defs>
        <linearGradient id="aiGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00a1ff" />
          <stop offset="100%" stopColor="#ff00fb" />
        </linearGradient>
      </defs>

      {/* First path */}
      <path
        d="M19.03 3.56c-1.67-1.39-3.74-2.3-6.03-2.51v2.01c1.73.19 3.31.88 4.61 1.92zM11 3.06V1.05c-2.29.2-4.36 1.12-6.03 2.51l1.42 1.42C7.69 3.94 9.27 3.25 11 3.06M4.98 6.39 3.56 4.97C2.17 6.64 1.26 8.71 1.05 11h2.01c.19-1.73.88-3.31 1.92-4.61M20.94 11h2.01c-.21-2.29-1.12-4.36-2.51-6.03l-1.42 1.42c1.04 1.3 1.73 2.88 1.92 4.61M7 12l3.44 1.56L12 17l1.56-3.44L17 12l-3.44-1.56L12 7l-1.56 3.44z"
        fill="url(#aiGradient)"
      />

      {/* Second path */}
      <path
        d="M12 21c-3.11 0-5.85-1.59-7.46-4H7v-2H1v6h2v-2.7c1.99 2.84 5.27 4.7 9 4.7 4.87 0 9-3.17 10.44-7.56l-1.96-.45C19.25 18.48 15.92 21 12 21"
        fill="url(#aiGradient)"
      />
    </SvgIcon>
  );
}

const EnhanceScriptAPI = async (text) => {
  const token = localStorage.getItem("token");
  const isLoggedIn = localStorage.getItem("isLoggedIn");

  try {
    const response = await fetch(`${sourceUrl}/heygen/script`, {
      method: "Post",
      headers: {
        "Content-Type": "application/json",
        ...(isLoggedIn && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        script: text,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (err) {
    return text;
  }
};

function VideoCreationDialog({
  open,
  onClose,
  videoTitle,
  timelineItems,
  onStart,
  caption,
  Width,
  Height,
}) {
  const [loading, setLoading] = useState(true);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("pending");
  const [statusMessage, setStatusMessage] = useState(
    "Initializing video generation..."
  );

  // Polling function to check video status
  const pollVideoStatus = async (statusId, initialUrl) => {
    const token = localStorage.getItem("token");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const maxAttempts = 100; // Max 500 seconds (100 * 5 seconds)
    const pollInterval = 5000; // Poll every 5 seconds
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        try {
          const response = await fetch(
            `${sourceUrl}/heygen/video/status/${statusId}`,
            {
              headers: {
                "Content-Type": "application/json",
                ...(isLoggedIn && { Authorization: `Bearer ${token}` }),
              },
            }
          );

          if (!response.ok) {
            let errorMessage = "Failed to check video status";
            try {
              const contentType = response.headers.get("content-type");
              if (contentType && contentType.includes("application/json")) {
                const err = await response.json();
                errorMessage =
                  err.message || `Status check failed (${response.status})`;
              } else {
                // Don't show raw HTML/text responses to user
                if (response.status === 500) {
                  errorMessage = "Server error while checking status.";
                } else if (response.status === 404) {
                  errorMessage =
                    "Video status not found. Please try regenerating.";
                } else {
                  errorMessage = `Status check failed (Error ${response.status})`;
                }
                console.error("Non-JSON status error:", await response.text());
              }
            } catch (e) {
              console.error("Error parsing status error response:", e);
              errorMessage = "Failed to check video status";
            }
            throw new Error(errorMessage);
          }

          const statusData = await response.json();

          // Update progress and status
          setProgress(statusData.progress || 0);
          setStatus(statusData.status);

          // Update status message based on progress
          if (statusData.progress < 25) {
            setStatusMessage("Processing your script...");
          } else if (statusData.progress < 50) {
            setStatusMessage("Generating avatar animations...");
          } else if (statusData.progress < 75) {
            setStatusMessage("Rendering video...");
          } else {
            setStatusMessage("Finalizing your video...");
          }

          if (statusData.status === "completed") {
            resolve({
              url: statusData.url || initialUrl,
              status: statusData,
            });
          } else if (statusData.status === "failed") {
            reject(new Error(statusData.error || "Video generation failed"));
          } else if (attempts >= maxAttempts) {
            reject(new Error("Video generation timed out. Please try again."));
          } else {
            // Continue polling
            attempts++;
            setTimeout(checkStatus, pollInterval);
          }
        } catch (error) {
          console.error("Error checking status:", error);
          if (attempts < 3) {
            // Retry a few times for network errors
            attempts++;
            setTimeout(checkStatus, pollInterval);
          } else {
            reject(error);
          }
        }
      };

      // Start polling
      checkStatus();
    });
  };

  useEffect(() => {
    const sendVideoData = async () => {
      if (!open) return;

      setLoading(true);
      setErrorMsg("");
      setDownloadUrl(null);
      setProgress(0);
      setStatus("pending");
      setStatusMessage("Initializing video generation...");

      const payloadItems = timelineItems.map((item) => ({
        avatar: item.avatar,
        talkingphoto: item.talkingphoto,
        voice: item.voice,
        script: item.script,
        background: item.background,
      }));

      const token = localStorage.getItem("token");
      const isLoggedIn = localStorage.getItem("isLoggedIn");

      try {
        const response = await fetch(`${sourceUrl}/heygen/video`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(isLoggedIn && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            title: videoTitle,
            caption: caption,
            width: Width,
            height: Height,
            items: payloadItems,
          }),
        });

        if (!response.ok) {
          let errorMessage = "Failed to generate video";
          try {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
              const err = await response.json();
              errorMessage = err.message || `Server error (${response.status})`;
            } else {
              // Don't show raw HTML/text responses to user
              if (response.status === 500) {
                errorMessage = "Server error. Please try again later.";
              } else if (response.status === 404) {
                errorMessage =
                  "Service not available. Please check your connection.";
              } else if (response.status === 401) {
                errorMessage = "Authentication failed. Please log in again.";
              } else if (response.status === 400) {
                errorMessage = "Invalid request. Please check your input.";
              } else {
                errorMessage = `Request failed (Error ${response.status})`;
              }
              console.error("Non-JSON error response:", await response.text());
            }
          } catch (e) {
            console.error("Error parsing error response:", e);
            errorMessage = "An unexpected error occurred";
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        console.log("Initial response:", data);

        // Check if we got the new async response format
        if (data.status_id) {
          // New async API - start polling
          setStatus("processing");
          const result = await pollVideoStatus(
            data.status_id,
            data.url || data.data
          );
          setDownloadUrl(result.url);
          setStatus("completed");
          setStatusMessage("Video ready for download!");
        } else {
          // Old sync API - video is ready immediately
          setDownloadUrl(data.data || data.url);
          setProgress(100);
          setStatus("completed");
          setStatusMessage("Video ready for download!");
        }
      } catch (err) {
        setErrorMsg(err.message);
        setStatus("failed");
        setStatusMessage("Video generation failed");
      } finally {
        setLoading(false);
      }
    };
    sendVideoData();
  }, [open]);

  const handlePrimaryClick = async () => {
    if (downloadUrl) {
      console.log("Opening download URL:", downloadUrl);

      // Test: Open the download link directly in a new tab
      // Check if downloadUrl already has https://
      const fullUrl = downloadUrl.startsWith("http")
        ? downloadUrl
        : `https://${downloadUrl}`;
      window.open(fullUrl, "_blank");

      // COMMENTED OUT FOR TESTING - Original download functionality
      /*
      const filename = videoTitle;
      const token = localStorage.getItem("token");
      const isLoggedIn = localStorage.getItem("isLoggedIn");

      try {
        // Fetch the file as a blob
        const response = await fetch("https://" + downloadUrl, {
          method: "GET",
          headers: {
            "Content-Type": "video/mp4",
            ...(isLoggedIn && { Authorization: `Bearer ${token}` }),
          },
        });
        //const response = await window.__originalFetch(downloadUrl);
        const blob = await response.blob();

        // Create a download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename + ".mp4";
        document.body.appendChild(link);
        link.click();

        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (error) {
        console.error("Download failed:", error);
        setErrorMsg(error?.message);
      }
      */
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={
        status === "completed" || status === "failed" ? onClose : undefined
      }
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {status === "completed"
          ? "Video Ready!"
          : status === "failed"
          ? "Generation Failed"
          : "Creating Video"}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ minHeight: 120 }}>
          {/* Processing State */}
          {status === "processing" && (
            <Box>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <CircularProgress size={24} />
                <Typography variant="body1">{statusMessage}</Typography>
              </Box>

              {/* Progress Bar */}
              <Box sx={{ width: "100%", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: "100%" }}>
                    <Box
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "rgba(0, 161, 255, 0.1)",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: `${progress}%`,
                          bgcolor: "#00a1ff",
                          borderRadius: 4,
                          transition: "width 0.5s ease-in-out",
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 45 }}
                  >
                    {progress}%
                  </Typography>
                </Box>
              </Box>

              <Typography variant="caption" color="text.secondary">
                This may take 2-5 minutes depending on video length
              </Typography>
            </Box>
          )}

          {/* Initial Loading State */}
          {status === "pending" && loading && (
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={24} />
              <Typography>{statusMessage}</Typography>
            </Box>
          )}

          {/* Success State */}
          {status === "completed" && !loading && (
            <Box textAlign="center" py={2}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  bgcolor: "success.light",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <Typography fontSize={30}>✓</Typography>
              </Box>
              <Typography variant="h6" gutterBottom>
                Your video is ready!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click Download to save your video
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {status === "failed" && (
            <Box textAlign="center" py={2}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  bgcolor: "error.light",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <Typography fontSize={30} color="error">
                  ✕
                </Typography>
              </Box>
              <Typography variant="h6" gutterBottom color="error">
                Generation Failed
              </Typography>
              <Typography variant="body2" color="error">
                {errorMsg || "An error occurred during video generation"}
              </Typography>
              <Typography
                variant="caption"
                display="block"
                mt={2}
                color="text.secondary"
              >
                Please try again or contact support if the issue persists
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {status === "processing" && (
          <Button onClick={onClose} disabled>
            Processing...
          </Button>
        )}
        {status === "pending" && !errorMsg && (
          <Button onClick={onClose}>Cancel</Button>
        )}
        {status === "failed" && (
          <Button onClick={onClose} color="error">
            Close
          </Button>
        )}
        {status === "completed" && (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePrimaryClick}
              startIcon={<Typography>🎬</Typography>}
            >
              View Video
              {/* Original text: Download Video */}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

const LeftPanel = () => {
  return (
    <Paper
      elevation={3}
      sx={{
        position: "fixed",
        left: 0,
        top: 0,
        width: "3.9rem",
        height: "100vh",
        bgcolor: "#0A0B13",
        color: "white",
        p: 2,
        overflow: "auto",
      }}
    >
      {/* Logo */}
      <Box sx={{ position: "absolute", left: 10 }}>
        <img
          src={ModalXLogo}
          style={{ width: 40, height: 40 }}
          alt="ModalX Logo"
        />
      </Box>

      {/* Video Creation Tool */}
      <Box
        sx={{
          position: "absolute",
          bottom: 30,
          left: 0,
        }}
      >
        <img
          src={HeygenLogo}
          style={{ width: 60, height: 30, transform: "rotate(-90deg)" }}
          alt="HeyGen Logo"
        />
      </Box>
    </Paper>
  );
};

const ScriptPanel = ({ script, onScriptChange, onAIEnhance, isEnhancing }) => {
  const charCount = script.length;
  const maxChars = 2000;

  return (
    <Paper
      elevation={2}
      sx={{
        position: "absolute",
        left: "4.2rem",
        //top: 303.5,
        top: "20vw",
        width: "33vw",
        //minHeight: 283.5,
        aspectRatio: 16 / 9,
        bgcolor: "#0e0f16",
        p: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          sx={{ color: "#8e8e8e", fontWeight: "bold", fontSize: "15px" }}
        >
          Script
        </Typography>
        <Button
          startIcon={
            isEnhancing ? <CircularProgress size={16} /> : <GradientAIIcon />
          }
          onClick={onAIEnhance}
          disabled={isEnhancing || !script.trim()}
          sx={{
            color: "white",
            "&:hover": {
              background:
                "linear-gradient(45deg, rgba(230, 0, 226, 0.2), rgba(0, 145, 230, 0.2))",
            },
            "&:disabled": {
              opacity: 0.8,
            },
          }}
        >
          <Typography sx={{ fontSize: "12px", color: "white" }}>
            {isEnhancing ? "Enhancing..." : "AI Enhance"}
          </Typography>
        </Button>
      </Box>

      <TextField
        multiline
        rows={(window.innerWidth * 0.3) / 60}
        fullWidth
        value={script}
        onChange={(e) => onScriptChange(e.target.value)}
        placeholder="Enter your script here..."
        inputProps={{ maxLength: maxChars }}
        sx={{
          "& .MuiOutlinedInput-root": {
            bgcolor: "transparent",
            color: "#b1b1b1",
            "& fieldset": {
              borderColor: "#333",
            },
            "&:hover fieldset": {
              borderColor: "#555",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#00a1ff",
            },
          },
          "& .MuiInputBase-input": {
            fontSize: "16px",
            fontFamily: "Jura, sans-serif",
            fontWeight: "bold",
          },
        }}
      />

      <Typography
        variant="caption"
        sx={{
          color: charCount > maxChars * 0.9 ? "#ff6b6b" : "#8e8e8e",
          float: "right",
        }}
      >
        {charCount}/{maxChars}
      </Typography>
    </Paper>
  );
};

const RightPanel = ({
  activeTab,
  onTabChange,
  timelineItemss,
  activeTimelineItems,
  items,
  caption,
  setCaption,
  width,
  setWidth,
  height,
  setHeight,
}) => {
  const renderTabContent = () => {
    {
      // switch (activeTab) {
      //   case "background":
      //     return (
      //       <Box sx={{ p: 2 }}>
      //         <Typography variant="h6" sx={{ color: "white", mb: 2 }}>
      //           Background Settings
      //         </Typography>
      //         <Typography variant="body2" sx={{ color: "#ccc", mb: 2 }}>
      //           Choose Background Color
      //         </Typography>
      //         <Grid container spacing={1}>
      //           {backgroundColors.map((color) => (
      //             <Grid item key={color}>
      //               <Box
      //                 sx={{
      //                   width: 40,
      //                   height: 40,
      //                   bgcolor: color,
      //                   border:
      //                     selectedColor === color
      //                       ? "3px solid #00a1ff"
      //                       : "1px solid #333",
      //                   borderRadius: 1,
      //                   cursor: "pointer",
      //                   "&:hover": {
      //                     transform: "scale(1.1)",
      //                   },
      //                 }}
      //                 onClick={() => setSelectedColor(color)}
      //               />
      //             </Grid>
      //           ))}
      //         </Grid>
      //         <Typography variant="body2" sx={{ color: "#ccc", mt: 2, mb: 1 }}>
      //           Selected Color: {selectedColor}
      //         </Typography>
      //       </Box>
      //     );
      //   case "avatars":
      //     return (
      //       <Box sx={{ p: 2 }}>
      //         <Typography variant="h6" sx={{ color: "white", mb: 2 }}>
      //           Personal Avatar
      //         </Typography>
      //         <Grid container spacing={2}>
      //           {avatars.map((avatar) => (
      //             <Grid item xs={6} key={avatar.id}>
      //               <Card
      //                 sx={{
      //                   bgcolor:
      //                     selectedAvatar === avatar.id ? "#2a2f4a" : "#1f233e",
      //                   cursor: "pointer",
      //                   border:
      //                     selectedAvatar === avatar.id
      //                       ? "2px solid #00a1ff"
      //                       : "none",
      //                   "&:hover": { bgcolor: "#2a2f4a" },
      //                 }}
      //                 onClick={() => setSelectedAvatar(avatar.id)}
      //               >
      //                 <CardContent sx={{ textAlign: "center", p: 2 }}>
      //                   <Avatar
      //                     sx={{
      //                       width: 60,
      //                       height: 60,
      //                       mx: "auto",
      //                       mb: 1,
      //                       bgcolor: "#00a1ff",
      //                     }}
      //                   >
      //                     {avatar.preview}
      //                   </Avatar>
      //                   <Typography variant="body2" sx={{ color: "white" }}>
      //                     {avatar.name}
      //                   </Typography>
      //                 </CardContent>
      //               </Card>
      //             </Grid>
      //           ))}
      //         </Grid>
      //       </Box>
      //     );
      //   case "voices":
      //     return (
      //       <Box sx={{ p: 2 }}>
      //         <Typography variant="h6" sx={{ color: "white", mb: 2 }}>
      //           Voice Selection
      //         </Typography>
      //         <Grid container spacing={1}>
      //           {voices.map((voice) => (
      //             <Grid item xs={12} key={voice.id}>
      //               <Card
      //                 sx={{
      //                   bgcolor:
      //                     selectedVoice === voice.id ? "#2a2f4a" : "#1f233e",
      //                   cursor: "pointer",
      //                   border:
      //                     selectedVoice === voice.id
      //                       ? "2px solid #00a1ff"
      //                       : "none",
      //                   "&:hover": { bgcolor: "#2a2f4a" },
      //                 }}
      //                 onClick={() => setSelectedVoice(voice.id)}
      //               >
      //                 <CardContent sx={{ p: 2 }}>
      //                   <Box
      //                     sx={{
      //                       display: "flex",
      //                       alignItems: "center",
      //                       justifyContent: "space-between",
      //                     }}
      //                   >
      //                     <Box>
      //                       <Typography
      //                         variant="body1"
      //                         sx={{ color: "white", fontWeight: "bold" }}
      //                       >
      //                         {voice.name}
      //                       </Typography>
      //                       <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
      //                         <Chip
      //                           label={voice.gender}
      //                           size="small"
      //                           sx={{ bgcolor: "#333", color: "#ccc" }}
      //                         />
      //                         <Chip
      //                           label={voice.accent}
      //                           size="small"
      //                           sx={{ bgcolor: "#333", color: "#ccc" }}
      //                         />
      //                       </Box>
      //                     </Box>
      //                     <IconButton sx={{ color: "#00a1ff" }}>
      //                       <PlayIcon />
      //                     </IconButton>
      //                   </Box>
      //                 </CardContent>
      //               </Card>
      //             </Grid>
      //           ))}
      //         </Grid>
      //       </Box>
      //     );
      //   case "settings":
      //     return (
      //       <Box sx={{ p: 2 }}>
      //         <Typography variant="h6" sx={{ color: "white", mb: 2 }}>
      //           Video Settings
      //         </Typography>
      //         <TextField
      //           fullWidth
      //           label="Video Quality"
      //           select
      //           defaultValue="1080p"
      //           sx={{ mb: 2 }}
      //           SelectProps={{
      //             native: true,
      //           }}
      //         >
      //           <option value="720p">720p HD</option>
      //           <option value="1080p">1080p Full HD</option>
      //           <option value="4K">4K Ultra HD</option>
      //         </TextField>
      //         <TextField
      //           fullWidth
      //           label="Frame Rate"
      //           select
      //           defaultValue="30"
      //           sx={{ mb: 2 }}
      //           SelectProps={{
      //             native: true,
      //           }}
      //         >
      //           <option value="24">24 FPS</option>
      //           <option value="30">30 FPS</option>
      //           <option value="60">60 FPS</option>
      //         </TextField>
      //         <TextField
      //           fullWidth
      //           label="Video Duration (seconds)"
      //           type="number"
      //           defaultValue={120}
      //           sx={{ mb: 2 }}
      //         />
      //       </Box>
      //     );
      //   default:
      //     return null;
      // }
    }

    switch (activeTab) {
      case "background":
        return (
          <HeygenVideoBackground
            timelineItems={timelineItemss}
            activeTimelineItem={activeTimelineItems}
            color={items[activeTimelineItems].background}
          />
        );
      case "avatars":
        return (
          <AvatarCreation
            timelineItems={timelineItemss}
            activeTimelineItem={activeTimelineItems}
          />
        );
      case "voices":
        return (
          <LanguageAndVoiceForHeygen
            timelineItems={timelineItemss}
            activeTimelineItem={activeTimelineItems}
          />
        );
      case "settings":
        return (
          <div>
            <CaptionSettingsForHeygen
              //timelineItems={timelineItemss}
              //activeTimelineItem={activeTimelineItems}
              state={caption}
              setState={setCaption}
              //state={items[activeTimelineItems].caption}
            />
            <DimensionsSettings
              //timelineItems={timelineItemss}
              //activeTimelineItem={activeTimelineItems}
              //width={items[activeTimelineItems].width}
              //height={items[activeTimelineItems].height}
              width={width}
              setWidth={setWidth}
              height={height}
              setHeight={setHeight}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: "absolute",
        right: 10,
        top: 10,
        width: "60vw",
        height: "69vh",
        bgcolor: "#12131c",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "#1a1d2e" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, value) => onTabChange(value)}
            sx={{
              marginLeft: "auto",
              marginRight: "auto",
              "& .MuiTab-root": {
                color: "#ccc",
                minHeight: 40,
                fontSize: "0.9rem",
              },
              "& .Mui-selected": {
                color: "#00a1ff",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#00a1ff",
              },
            }}
          >
            <Tab label="Background" value="background" />
            <Tab label="Avatars" value="avatars" />
            <Tab label="Voices" value="voices" />
            <Tab label="Settings" value="settings" />
          </Tabs>
          {/* <IconButton
            onClick={console.log("Clicked Close")}
            sx={{ color: "#8e8e8e" }}
          >
            <CloseIcon />
          </IconButton> */}
        </Box>
      </Box>
      <Box sx={{ height: "calc(100% - 64px)", overflow: "auto", p: 3 }}>
        {renderTabContent()}
      </Box>
    </Paper>
  );
};

const Timeline = ({
  timelineItems,
  onReorder,
  onDeleteItem,
  onAddToTimeline,
  onClickItem,
  activeItem,
}) => {
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    onReorder(result.source.index, result.destination.index);
  };

  return (
    <Paper
      elevation={2}
      sx={{
        position: "absolute",
        left: "3.9rem",
        bottom: 0,
        width: "calc(100vw - 3.8rem)",
        height: "19vh",
        bgcolor: "#12131c",
        overflow: "auto",
      }}
    >
      {/* Rulers  */}
      <Box
        sx={{
          position: "absolute",
          display: "flex",
          mb: 1,
          paddingLeft: 9,
          top: 10,
        }}
      >
        {Array.from({ length: 9 }, (_, i) => (
          <Typography
            key={i}
            variant="caption"
            sx={{
              color: "#555",
              minWidth: 155,
              textAlign: "center",
            }}
          >
            {(i + 1) * 20}S
          </Typography>
        ))}
      </Box>

      {/* Question Mark */}
      <Box
        sx={{
          position: "absolute",
          display: "flex",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Tooltip
          title={
            "Time Shown are Estimate and are calculated relative to previous attempts of video generation, which may not be 100% accurate."
          }
        >
          <IconButton size="small" sx={{ color: "#707070" }}>
            <HelpIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Timeline Items */}
      <Box sx={{ position: "absolute", top: 30 }}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="timeline" direction="horizontal">
            {(provided, snapshot) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: 80,
                  border: snapshot.isDraggingOver
                    ? "2px dashed #00a1ff"
                    : "2px dashed transparent",
                  borderRadius: 1,
                  p: 1,
                  transition: "border-color 0.3s ease",
                }}
              >
                {timelineItems.length === 0 ? (
                  <Box
                    sx={{ flex: 1, textAlign: "center", color: "#666", py: 2 }}
                  >
                    <Typography variant="body2">
                      create your video timeline
                    </Typography>
                  </Box>
                ) : (
                  timelineItems.map((item, index) => (
                    <Draggable
                      key={item.id}
                      draggableId={item.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <Card
                          onClick={() => onClickItem(index)}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{
                            minWidth: 100,
                            height: 80,
                            mr: 1,
                            bgcolor: snapshot.isDragging
                              ? "#2a2f4a"
                              : "#1f233e",
                            cursor: "grab",
                            "&:active": { cursor: "grabbing" },
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: 2,
                            transform: snapshot.isDragging
                              ? "rotate(5deg)"
                              : "none",
                          }}
                        >
                          <CardMedia
                            component="img"
                            src={item.thumbnail}
                            alt="Avatar Image"
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              zIndex: 1,
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              background:
                                index === activeItem
                                  ? "linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 208, 255, 0.5))"
                                  : "linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0))",
                              zIndex: 2,
                            }}
                          />
                          <CardContent
                            sx={{
                              p: 1,
                              height: "100%",
                              position: "relative",
                              zIndex: 3,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <DragIcon
                                sx={{
                                  color: "#666",
                                  fontSize: 16,
                                  mr: 0.5,
                                }}
                              />
                              {/* <Typography
                                variant="caption"
                                sx={{
                                  color: "white",
                                  fontSize: 10,
                                  marginLeft: "25%",
                                }}
                              >
                                {index}
                              </Typography> */}
                            </Box>
                            {/* <Typography
                              variant="caption"
                              sx={{
                                color: "#ccc",
                                fontSize: 10,
                                marginLeft: "40%",
                              }}
                            >
                              {item.duration}s
                            </Typography> */}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteItem(item.id);
                              }}
                              sx={{
                                position: "absolute",
                                top: 2,
                                right: 2,
                                color: "#ff6b6b",
                                fontSize: 12,
                                p: 0.5,
                              }}
                            >
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </CardContent>
                        </Card>
                      )}
                    </Draggable>
                  ))
                )}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>

      {/* Add Timeline Item Button */}
      <Tooltip title="Add new timeline item">
        <IconButton
          onClick={onAddToTimeline}
          sx={{
            position: "absolute",
            right: 20,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: "#1D1D1D",
            color: "#FFBB00",
            "&:hover": {
              bgcolor: "#2D2D2D",
            },
          }}
        >
          <AddIcon />
        </IconButton>
      </Tooltip>
    </Paper>
  );
};

export default function VideoGenerate() {
  const navigate = useNavigate();
  const {setSelectedVideoGenerationPlatform} = useWorkspace()
  // Check authentication on component mount
  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (!isLoggedIn) {
      alert("Please login to access this feature");
      navigate(-1); // Go back to previous page
    }
  }, [navigate]);

  const [script, setScript] = useState(
    "Hey I will be talking about how we generate video with\nModalX, hope you like it."
  );
  const [caption, setCaption] = useState(false);
  const [videoWidth, setVideoWidth] = useState(1920);
  const [videoHeight, setVideoHeight] = useState(1080);

  const [videoTitle, setVideoTitle] = useState("Heygen Video Title");
  const [activeTab, setActiveTab] = useState("background");
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [activeTimelineItem, setActiveTimelineItem] = useState(0);
  const [timelineItems, setTimelineItems] = useState([
    {
      id: `timeline-${Date.now()}`,
      thumbnail:
        "https://files2.heygen.ai/avatar/v3/00de8c52f5884155997a643d9732e669_37110/preview_talk_3.webp",
      avatar: "Anja_sitting_sofa_front",
      talkingphoto: "",
      voice: "73c0b6a2e29d4d38aca41454bf58c955",
      script:
        "Hey I will be talking about how we generate video with\nModalX, hope you like it.",
      duration: 10,
      position: 0,
      //width: 1920,
      //height: 1080,
      //caption: false,
      background: "",
    },
  ]);
  useEffect(() => {
    const current = timelineItems[activeTimelineItem];
    if (current) {
      setScript(current.script);
    }
  }, [activeTimelineItem]);

  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const handleScriptChange = (newScript) => {
    setScript(newScript);
    setTimelineItems((prevItems) => {
      const items = [...prevItems];

      items[activeTimelineItem] = {
        ...items[activeTimelineItem],
        script: newScript,
      };

      return items;
    });
  };
  const handleAIEnhance = async () => {
    if (!script.trim()) return;
    setIsEnhancing(true);

    try {
      const enhancedScript = await EnhanceScriptAPI(script, setScript);
      handleScriptChange(enhancedScript);
      showSnackbar("Script Enhanced successfully!", "success");
    } catch (error) {
      showSnackbar("Failed to enhance script. Please try again", "error");
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleTimelineReorder = (sourceIndex, destinationIndex) => {
    const newItems = Array.from(timelineItems);
    const [reorderedItem] = newItems.splice(sourceIndex, 1);
    newItems.splice(destinationIndex, 0, reorderedItem);

    const updatedItems = newItems.map((item, index) => ({
      ...item,
      position: index,
    }));

    setTimelineItems(updatedItems);
  };
  const handleDeleteTimelineItem = (itemId) => {
    if (timelineItems.length === 1) {
      const newItem = {
        id: `timeline-${Date.now()}`,
        thumbnail:
          "https://files2.heygen.ai/avatar/v3/00de8c52f5884155997a643d9732e669_37110/preview_talk_3.webp",
        avatar: "Anja_sitting_sofa_front",
        talkingphoto: "",
        voice: "73c0b6a2e29d4d38aca41454bf58c955",
        script:
          "Hey I will be talking about how we generate video with\nModalX, hope you like it.",
        duration: 10,
        position: 0,
        //width: 1920,
        //height: 1080,
        //caption: false,
        background: "",
      };
      setTimelineItems([newItem]);
      setActiveTimelineItem(0);
    } else if (timelineItems[activeTimelineItem].id >= itemId) {
      const prevIndex =
        activeTimelineItem === 0
          ? timelineItems.length === 1
            ? -1
            : 0
          : activeTimelineItem - 1;
      setActiveTimelineItem(prevIndex);
    }
    setTimelineItems((prev) => prev.filter((item) => item.id !== itemId));
    showSnackbar("Timeline item removed", "info");
  };
  const handleAddToTimeline = () => {
    const newItem = {
      id: `timeline-${Date.now()}`,
      thumbnail:
        "https://files2.heygen.ai/avatar/v3/00de8c52f5884155997a643d9732e669_37110/preview_talk_3.webp",
      avatar: "Anja_sitting_sofa_front",
      talkingphoto: "",
      voice: "73c0b6a2e29d4d38aca41454bf58c955",
      script:
        "Hey I will be talking about how we generate video with\nModalX, hope you like it.",
      duration: 10,
      position: timelineItems.length,
      //width: 1920,
      //height: 1080,
      //caption: false,
      background: "",
    };
    setActiveTimelineItem(newItem.position);
    setTimelineItems((prev) => [...prev, newItem]);
  };

  const handleCreate = async () => {
    if (timelineItems.length === 0) {
      showSnackbar(
        "Please add items to the timeline before creating",
        "warning"
      );
      return;
    }

    const emptyIndex = timelineItems.findIndex(
      (item) => item.script.trim().length === 0
    );

    if (emptyIndex !== -1) {
      showSnackbar(
        `Please add a script on Video ${emptyIndex + 1} before creating`,
        "warning"
      );
      return;
    }

    setOpenDialog(true);
    // showConfirmDialog(
    //   "Create Video",
    //   "Are you ready to create your video? This process may take a few minutes.",
    //   async () => {
    //     showSnackbar(
    //       "Video creation started! You will be notified when ready.",
    //       "success"
    //     );
    //     await new Promise((resolve) => setTimeout(resolve, 20000));
    //     console.log("Creating video with:", {
    //       videoTitle,
    //       timelineItems,
    //       totalDuration: timelineItems.reduce(
    //         (sum, item) => sum + item.duration,
    //         0
    //       ),
    //     });
    //   }
    // );
  };
  const handleCancel = () => {
    showConfirmDialog(
      "Cancel Video Creation",
      "Are you sure you want to cancel? All changes will be lost.",
      () => {
        // Navigate back to the previous page
        navigate("/workspace");
        setSelectedVideoGenerationPlatform("Avatar");
      }
    );
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };
  const showConfirmDialog = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  return (
    <Box
      sx={{
        bgcolor: "#16192C",
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Left Panel */}
      <LeftPanel />

      {/* Video Preview Area */}
      <Paper
        elevation={2}
        sx={{
          position: "absolute",
          left: "4.2rem",
          top: 10,
          width: "33vw",
          aspectRatio: 16 / 9,
          bgcolor: "#0A0B13",
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          "&:hover": { bgcolor: "#1e2236", border: "1px solid white" },
        }}
      >
        <Box
          component="img"
          src={
            activeTimelineItem !== -1
              ? timelineItems[activeTimelineItem].thumbnail
              : null
          }
          sx={{
            borderRadius: 2,
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 1,
          }}
        />
        <Box sx={{ textAlign: "center", color: "#666" }}>
          <Typography variant="h6">Video Preview</Typography>
        </Box>
      </Paper>

      {/* Script Panel */}
      <ScriptPanel
        script={script}
        onScriptChange={handleScriptChange}
        onAIEnhance={handleAIEnhance}
        isEnhancing={isEnhancing}
      />

      {/* Right Panel */}
      <RightPanel
        activeTab={activeTab}
        onTabChange={setActiveTab}
        timelineItemss={setTimelineItems}
        items={timelineItems}
        activeTimelineItems={activeTimelineItem}
        caption={caption}
        setCaption={setCaption}
        width={videoWidth}
        setWidth={setVideoWidth}
        height={videoHeight}
        setHeight={setVideoHeight}
      />

      {/* Video Title Input  */}
      <Paper
        elevation={2}
        sx={{
          position: "absolute",
          right: 220,
          top: "72vh",
          width: 433,
          height: 50,
          bgcolor: "#12131c",
          borderRadius: 2.5,
        }}
      >
        <TextField
          fullWidth
          value={videoTitle}
          onChange={(e) => setVideoTitle(e.target.value)}
          placeholder="HeyGen Video Title"
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 50,
              bgcolor: "transparent",
              color: "#7c7c7c",
              border: "none",
              "& fieldset": {
                border: "none",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: 20,
              fontWeight: "bold",
              fontFamily: "Jura, sans-serif",
              px: 2,
            },
          }}
        />
      </Paper>

      {/* Create and Cancel Buttons  */}
      <Box
        sx={{
          position: "absolute",
          right: 20,
          top: "72vh",
          display: "flex",
          gap: 2,
        }}
      >
        <Button
          variant="text"
          onClick={handleCancel}
          sx={{
            color: "#ffffff",
            fontSize: 16,
            fontWeight: "bold",
            borderRadius: 4,
            "&:hover": { bgcolor: "rgba(255,255,255,0.1)", borderRadius: 4 },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          sx={{
            bgcolor: "rgba(246,192,111,0.5)",
            color: "#ffffff",
            fontSize: 16,
            fontWeight: "bold",
            px: 2,
            py: 1,
            borderRadius: 4,
            "&:hover": { bgcolor: "rgba(246,192,111,0.7)" },
          }}
        >
          Create
        </Button>
      </Box>

      {/* Timeline */}
      <Timeline
        timelineItems={timelineItems}
        onReorder={handleTimelineReorder}
        onDeleteItem={handleDeleteTimelineItem}
        onAddToTimeline={handleAddToTimeline}
        onClickItem={setActiveTimelineItem}
        activeItem={activeTimelineItem}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        PaperProps={{
          sx: {
            bgcolor: "#1a1d2e",
            color: "white",
          },
        }}
      >
        <DialogTitle sx={{ color: "white" }}>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#ccc" }}>
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            sx={{ color: "#ccc" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              confirmDialog.onConfirm?.();
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            variant="contained"
            sx={{ bgcolor: "#00a1ff" }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Creation Dialog  */}
      <VideoCreationDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        videoTitle={videoTitle}
        caption={caption}
        Width={videoWidth}
        Height={videoHeight}
        timelineItems={timelineItems}
        onStart={() =>
          showSnackbar(
            "Video creation started! You will be notified when ready.",
            "success"
          )
        }
      />
    </Box>
  );
}
